// 門鈴頁［🚪 開門］（docs/panel 05-ui-ux V-14、SD §7.2a / §7.3）。
// 手機是真人帳號：signaling 依字典 write_authz=door_open 檢查（主人一律可以），不用控制票。
// 送 0x80E0 Unlock op:set（rawVal {request_id, hold_s}；request_id 由 App 產生）→ 等門鈴的 Unlocked 事件：
//   ok「門已開」、fail「門沒有打開」＋［再試一次］（新編號）、10 秒沒結果「結果不明」＋［再送一次］（同編號）、
//   duplicate 依 orig_result。不自動重送。
import type { SignalFrame } from '../../lib/ws';
import type { DeviceSummary } from '../devices/deviceModel';

export const UNLOCK_CMD_ID = '0x31000080E0000000';
export const UNLOCKED_EVENT_ID = '0x11000080e0000000';
export const DOOR_RESULT_TIMEOUT_MS = 10_000;
export const DOOR_HOLD_S = 3;

export type DoorPhase = 'idle' | 'confirm' | 'sending' | 'ok' | 'fail' | 'unknown' | 'error';

/** 有開門 cluster（0x80E0）且主人或有「可開門」權限才顯示 */
export function canOpenDoor(d: Pick<DeviceSummary, 'role' | 'permissions' | 'capability_effective'> | null | undefined): boolean {
  if (!d) return false;
  const has = (d.capability_effective ?? []).some((c) => (c.cluster ?? '').toLowerCase() === '0x80e0');
  return has && (d.role === 'owner' || (d.permissions ?? []).includes('door_open'));
}

export function newDoorRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return 'door-' + Math.random().toString(16).slice(2) + Date.now().toString(16);
}

/** sendCmd 的 extra：rawVal 物件（門鈴兩種都收，送物件） */
export function unlockExtra(requestId: string, holdS = DOOR_HOLD_S): Record<string, unknown> {
  return { rawVal: { request_id: requestId, hold_s: holdS } };
}

/** 門鈴的 Unlocked 事件 → 這一筆（request_id 相同）的最終結果；不是這一筆回 null */
export function unlockedResult(frame: SignalFrame, deviceId: string, requestId: string): 'ok' | 'fail' | null {
  if (frame.type !== 'event' || String(frame.event_id ?? '').toLowerCase() !== UNLOCKED_EVENT_ID || frame.device_id !== deviceId) return null;
  let pl: unknown = frame.payload;
  if (typeof pl === 'string') {
    try {
      pl = JSON.parse(pl);
    } catch {
      return null;
    }
  }
  const p = (pl ?? {}) as { request_id?: string; result?: string; orig_result?: string };
  if (p.request_id !== requestId) return null;
  const r = p.result === 'duplicate' ? p.orig_result : p.result;
  return r === 'ok' ? 'ok' : r === 'fail' ? 'fail' : null;
}

/** 開門流程需要的 signaling 介面（測試用替身） */
export interface DoorSignaling {
  sendCmd(deviceId: string, op: 'set', id: string, extra: Record<string, unknown>): Promise<SignalFrame>;
  on(ev: 'event', cb: (f: SignalFrame) => void): () => void;
}

/**
 * 一次開門：send(retry) — retry=true 時沿用上次的 request_id（只在結果不明後用）。
 * onPhase 收 sending / ok / fail / unknown / error。
 */
export class DoorOpener {
  private requestId = '';
  private off: (() => void) | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private sig: DoorSignaling,
    private deviceId: string,
    private onPhase: (p: DoorPhase) => void,
    private timeoutMs = DOOR_RESULT_TIMEOUT_MS,
  ) {}

  get currentRequestId(): string {
    return this.requestId;
  }

  send(sameId = false): void {
    this.stop();
    if (!sameId || !this.requestId) this.requestId = newDoorRequestId();
    const rid = this.requestId;
    let done = false;
    const finish = (p: DoorPhase) => {
      if (done) return;
      done = true;
      this.stop();
      this.onPhase(p);
    };
    this.onPhase('sending');
    this.off = this.sig.on('event', (f) => {
      const r = unlockedResult(f, this.deviceId, rid);
      if (r) finish(r);
    });
    this.timer = setTimeout(() => finish('unknown'), this.timeoutMs);
    this.sig.sendCmd(this.deviceId, 'set', UNLOCK_CMD_ID, unlockExtra(rid)).catch(() => finish('error'));
  }

  stop(): void {
    if (this.off) this.off();
    this.off = null;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }
}
